// The in-memory API's drift audits (DC-10; services/api/src/routes/agents/drift-audits.ts and
// jobs/drift-audit.ts), with the real routes' rules: the `agents` add-on gates both routes (403
// `feature_disabled`); company managers only (owner, admin, the company's agency operator; anyone
// else 403, a DE of another company 404); the list is newest first; a request is `manual` or
// `target_changed` (422 otherwise) and needs a DE that runs a deployed version (the deploy
// mirror, `FakeDe.deploy`) with no open audit (409 `conflict` otherwise). The worker's run job is
// played by time: `RUN_AFTER_MS` after its `scheduled_for` an audit is `running`, `END_AFTER_MS`
// later `passed`, its evals all `pass` and its cost the fake's tool-call price per eval. The seed
// gives every deployed DE a monthly audit that passed and, for Riley, one after a target-system
// change that failed (with the supervisor's task).
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, page, problem } from "./fake";
import { type FakeDe, uid } from "./fake-data";

type DriftAudit = OperationOutput<"digitalEmployees.driftAudits">["data"][number];

/** Simulated run job: scheduled → running → passed. */
export const RUN_AFTER_MS = 1500;
export const END_AFTER_MS = 1500;
/** The audited evals of every fake deployed version, and the fake's `tool_call` price. */
export const FAKE_AUDIT_EVALS = ["stays-on-board", "confirms-slots"] as const;
export const FAKE_TOOL_CALL_MICROS = 2_500;

export type DriftDeps = {
  now: () => Date;
  desOf: (companyId: string) => FakeDe[];
  agentsOn: (p: Principal) => boolean;
};

const DAY = 86_400_000;

export function createDriftAuditHandlers(deps: DriftDeps): Partial<Record<RouteId, Handler>> {
  const audits = new Map<string, DriftAudit[]>();
  let seq = 0x100;

  const passed = Object.fromEntries(FAKE_AUDIT_EVALS.map((k) => [k, "pass" as const]));
  const cost = FAKE_AUDIT_EVALS.length * FAKE_TOOL_CALL_MICROS;

  function row(
    companyId: string,
    de: FakeDe,
    o: Partial<DriftAudit> & { scheduled_for: string },
  ): DriftAudit {
    const versionId = de.deploy?.template_version_id ?? uid(0x7d00, 0);
    return {
      company_id: companyId,
      id: uid(0x7f00, ++seq),
      de_membership_id: de.membership_id,
      template_version_id: versionId,
      trigger: "schedule",
      started_at: null,
      ended_at: null,
      state: "scheduled",
      results: {},
      task_id: null,
      cost_micros: 0,
      actor_kind: "system",
      actor_membership_id: null,
      created_at: o.scheduled_for,
      updated_at: o.scheduled_for,
      ...o,
    };
  }

  function seed(companyId: string): DriftAudit[] {
    const have = audits.get(companyId);
    if (have) return have;
    const list: DriftAudit[] = [];
    const now = deps.now().getTime();
    for (const de of deps.desOf(companyId)) {
      if (!de.deploy) continue;
      const at = (days: number, plusMs = 0) => new Date(now - days * DAY + plusMs).toISOString();
      list.push(
        row(companyId, de, {
          scheduled_for: at(31),
          started_at: at(31, 60_000),
          ended_at: at(31, 75_000),
          state: "passed",
          results: passed,
          cost_micros: cost,
          updated_at: at(31, 75_000),
        }),
      );
      if (de.display_name.startsWith("Riley"))
        list.push(
          row(companyId, de, {
            trigger: "target_changed",
            scheduled_for: at(4),
            started_at: at(4, 60_000),
            ended_at: at(4, 80_000),
            state: "failed",
            results: { "stays-on-board": "pass", "confirms-slots": "fail" },
            task_id: uid(0x7f80, Number.parseInt(de.membership_id.slice(-4), 16)),
            cost_micros: cost,
            actor_kind: "person",
            actor_membership_id: de.supervisor.membership_id,
            updated_at: at(4, 80_000),
          }),
        );
    }
    audits.set(companyId, list);
    return list;
  }

  /** Time plays the worker's `drift_audit.run`. */
  function tick(list: DriftAudit[]) {
    const now = deps.now().getTime();
    for (const a of list) {
      const due = Date.parse(a.scheduled_for) + RUN_AFTER_MS;
      if (a.state === "scheduled" && now >= due) {
        a.state = "running";
        a.started_at = new Date(due).toISOString();
        a.updated_at = a.started_at;
      }
      if (a.state === "running" && a.started_at) {
        const end = Date.parse(a.started_at) + END_AFTER_MS;
        if (now >= end) {
          a.state = "passed";
          a.ended_at = new Date(end).toISOString();
          a.results = { ...passed };
          a.cost_micros = cost;
          a.updated_at = a.ended_at;
        }
      }
    }
  }

  function featureOff(): Response {
    return new Response(
      JSON.stringify({
        type: "https://companyos.app/errors/feature_disabled",
        title: "Feature not enabled",
        status: 403,
        code: "feature_disabled",
        feature: "agents",
        request_id: "fake-drift-audits",
        detail: "The agents add-on is not enabled for this company.",
      }),
      { status: 403, headers: { "content-type": "application/problem+json" } },
    );
  }

  /** The DE a manager names, or the refusal the API gives. */
  function admitted(p: Principal | null, id: string | undefined): FakeDe | Response {
    if (!p) return notFound();
    if (!deps.agentsOn(p)) return featureOff();
    if (!p.manager)
      return problem(403, "forbidden", "Forbidden", "Company managers read and request audits.");
    const de = deps.desOf(p.company.id).find((d) => d.membership_id === id);
    return de ?? notFound();
  }

  return {
    "digitalEmployees.driftAudits": ({ principal: p, params, url }) => {
      const de = admitted(p, params.id);
      if (de instanceof Response) return de;
      const list = seed((p as Principal).company.id);
      tick(list);
      const rows = list
        .filter((a) => a.de_membership_id === de.membership_id)
        .sort((a, b) => (a.id < b.id ? 1 : -1));
      return json(page(rows, url));
    },
    "digitalEmployees.requestDriftAudit": ({ principal: p, params, body }) => {
      const de = admitted(p, params.id);
      if (de instanceof Response) return de;
      const b = (body ?? {}) as { trigger?: unknown; scheduled_for?: unknown };
      if (b.trigger !== "manual" && b.trigger !== "target_changed")
        return problem(
          422,
          "validation_failed",
          "Validation failed",
          "trigger: manual or target_changed",
        );
      const companyId = (p as Principal).company.id;
      const list = seed(companyId);
      tick(list);
      if (!de.deploy)
        return problem(
          409,
          "conflict",
          "Conflict",
          "the digital employee runs no deployed template version: nothing to audit",
        );
      if (
        list.some(
          (a) =>
            a.de_membership_id === de.membership_id &&
            (a.state === "scheduled" || a.state === "running"),
        )
      )
        return problem(
          409,
          "conflict",
          "Conflict",
          "an audit of this digital employee is already scheduled or running",
        );
      const at = deps.now().toISOString();
      const membership =
        (p as Principal).me.memberships.find((m) => m.company_id === companyId)?.membership_id ??
        null;
      const created = row(companyId, de, {
        trigger: b.trigger,
        scheduled_for: typeof b.scheduled_for === "string" ? b.scheduled_for : at,
        actor_kind: membership ? "person" : "agency_user",
        actor_membership_id: membership,
        created_at: at,
        updated_at: at,
      });
      list.push(created);
      return json(created, 201);
    },
  };
}
